/**
 * Конфигурация API для взаимодействия с backend
 */
import { getToken, clearToken } from '../api/tokenStore';
import { refresh } from '../api/authApi';
import { waitForReAuth } from '../api/reAuthGate';

const isDevelopment = process.env.NODE_ENV === 'development';
// In production the browser calls the same host — ingress proxies to the right backend.
const API_BASE_URL = isDevelopment ? 'http://localhost:8090' : '/api/diagrams';
const DISCOVERY_SERVICE_URL = isDevelopment ? 'http://localhost:8070/api/k8s' : '/api/discovery/api/k8s';

export const API_CONFIG = {
  baseURL: API_BASE_URL,
  discoveryServiceURL: DISCOVERY_SERVICE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
};

export const projectPath = (projectId, path) => `/projects/${projectId}${path}`;

export const API_ENDPOINTS = {
  // ─── Diagrams (project-scoped) ───────────────────────────────────────────────
  diagrams: {
    list: (pid) => `/projects/${pid}/diagrams/list`,
    listFull: (pid) => `/projects/${pid}/diagrams`,
    create: (pid) => `/projects/${pid}/diagrams`,
    getById: (pid, id) => `/projects/${pid}/diagrams/${id}`,
    update: (pid, id) => `/projects/${pid}/diagrams/${id}`,
    delete: (pid, id) => `/projects/${pid}/diagrams/${id}`,
    getDiagramSystems: (pid, id) => `/projects/${pid}/diagrams/${id}/systems`,
    getDiagramEntries: (pid, id) => `/projects/${pid}/diagrams/${id}/entries`,
    publish: (pid, id) => `/projects/${pid}/diagrams/${id}/publish`,
    pull: (pid, id) => `/projects/${pid}/diagrams/${id}/pull`,
  },

  // ─── Meta-Registries (project-scoped) ──────────────────────────────────────
  registries: {
    list: (pid) => `/projects/${pid}/meta-registries`,
    create: (pid) => `/projects/${pid}/meta-registries`,
    getBySlug: (pid, slug) => `/projects/${pid}/meta-registries/${slug}`,
    update: (pid, slug) => `/projects/${pid}/meta-registries/${slug}`,
    delete: (pid, slug) => `/projects/${pid}/meta-registries/${slug}`,
    entries: (pid, slug) => `/projects/${pid}/meta-registries/${slug}/entries`,
    entryByCode: (pid, slug, code) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/code/${encodeURIComponent(code)}`,
    entryById: (pid, slug, id) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/${id}`,
    entryChildren: (pid, slug, id) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/${id}/children`,
    entryDiagrams: (pid, slug, id) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/${id}/diagrams`,
    entryRelations: (pid, slug, id) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/${id}/relations`,
    publishEntry: (pid, slug, id) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/${id}/publish`,
    pullEntry: (pid, slug, id) => `/projects/${pid}/meta-registries/${encodeURIComponent(slug)}/entries/${id}/pull`,
  },

  // ─── Built-in systems registry ──────────────────────────────────────────────
  SYSTEMS_SLUG: 'information_systems',

  // ─── Relations (project-scoped) ─────────────────────────────────────────────
  relations: {
    list: (pid) => `/projects/${pid}/relations`,
    create: (pid) => `/projects/${pid}/relations`,
    delete: (pid, id) => `/projects/${pid}/relations/${id}`,
    types: (pid) => `/projects/${pid}/relation-types`,
    createType: (pid) => `/projects/${pid}/relation-types`,
    updateType: (pid, id) => `/projects/${pid}/relation-types/${id}`,
    deleteType: (pid, id) => `/projects/${pid}/relation-types/${id}`,
    rules: (pid) => `/projects/${pid}/relation-rules`,
    createRule: (pid) => `/projects/${pid}/relation-rules`,
    deleteRule: (pid, id) => `/projects/${pid}/relation-rules/${id}`,
  },

  // ─── Systems compat (project-scoped) ────────────────────────────────────────
  systemsCompat: {
    k8sMapping: (pid) => `/projects/${pid}/systems/k8s-mapping`,
    infrastructure: (pid, code) => `/projects/${pid}/systems/code/${encodeURIComponent(code)}/infrastructure`,
  },

  // ─── BCM (project-scoped) ───────────────────────────────────────────────────
  bcm: {
    defaultMap: (pid) => `/projects/${pid}/bcm/default-map`,
    nodes: (pid, mapId) => `/projects/${pid}/bcm/maps/${mapId}/nodes`,
    node: (pid, mapId, nodeId) => `/projects/${pid}/bcm/maps/${mapId}/nodes/${nodeId}`,
    sync: (pid, mapId) => `/projects/${pid}/bcm/maps/${mapId}/sync`,
  },

  // ─── Fitness Functions (project-scoped) ────────────────────────────────────
  fitnessFunctions: {
    list: (pid) => `/projects/${pid}/fitness-functions`,
    create: (pid) => `/projects/${pid}/fitness-functions`,
    update: (pid, id) => `/projects/${pid}/fitness-functions/${id}`,
    delete: (pid, id) => `/projects/${pid}/fitness-functions/${id}`,
  },

  // ─── Users ──────────────────────────────────────────────────────────────────
  users: {
    create: '/users',
    auth: '/users/auth',
    getById: (id) => `/users/${id}`,
  },

  // ─── Admin — GitLab ─────────────────────────────────────────────────────────
  adminGitlab: {
    connections: '/admin/gitlab/connections',
    connection: (id) => `/admin/gitlab/connections/${id}`,
    testConnection: (id) => `/admin/gitlab/connections/${id}/test`,
    triggerPull: (id) => `/admin/gitlab/connections/${id}/pull`,
  },

  // ─── Admin — Sync Conflicts ──────────────────────────────────────────────────
  adminConflicts: {
    list: '/admin/sync-conflicts',
    acceptGitlab: (id) => `/admin/sync-conflicts/${id}/accept-gitlab`,
    acceptPlatform: (id) => `/admin/sync-conflicts/${id}/accept-platform`,
  },

  // ─── Diagram metamodels (project-scoped) ────────────────────────────────────
  metamodels: {
    list:    (pid)     => `/projects/${pid}/metamodels`,
    getById: (pid, id) => `/projects/${pid}/metamodels/${id}`,
    create:  (pid)     => `/projects/${pid}/metamodels`,
    update:  (pid, id) => `/projects/${pid}/metamodels/${id}`,
    delete:  (pid, id) => `/projects/${pid}/metamodels/${id}`,
    publish: (pid, id) => `/projects/${pid}/metamodels/${id}/publish`,
    pull:    (pid, id) => `/projects/${pid}/metamodels/${id}/pull`,
  },

  // ─── K8s Discovery Service (used with DISCOVERY_SERVICE_URL as base) ─────────
  k8s: {
    // Public
    clusterConfigs: '/clusters/configs',
    platformResources: (configId) => `/clusters/${configId}/platform-resources`,
    // Discovery (fire-and-forget)
    startDiscoveryAll: '/discovery/start-all',
    startDiscovery: (configId) => `/discovery/${configId}/start`,
    // Admin
    adminClusters: '/admin/clusters',
    adminCluster: (id) => `/admin/clusters/${id}`,
    activateCluster: (id) => `/admin/clusters/${id}/activate`,
    deactivateCluster: (id) => `/admin/clusters/${id}/deactivate`,
    clusterHealth: (id) => `/admin/clusters/${id}/health`,
    testCluster: (id) => `/admin/clusters/${id}/test`,
  },
};

/**
 * Хелпер для создания полного URL
 */
export const createApiUrl = (endpoint, useDiscoveryService = false) => {
  const baseUrl = useDiscoveryService ? DISCOVERY_SERVICE_URL : API_BASE_URL;
  return `${baseUrl}${endpoint}`;
};

/**
 * Хелпер для обработки ошибок API
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Сервер вернул ответ с кодом ошибки
    const message = error.response.data?.message || error.response.data?.error || error.message;
    throw new Error(`API Error (${error.response.status}): ${message}`);
  } else if (error.request) {
    // Запрос был отправлен, но ответа не получено
    throw new Error('Сервер не отвечает. Проверьте подключение.');
  } else {
    // Ошибка при настройке запроса
    throw new Error(error.message);
  }
};

/**
 * Универсальная функция для выполнения запросов к API
 * Автоматически прикрепляет JWT, обновляет токен при 401 и повторяет запрос.
 */
export const apiRequest = async (endpoint, options = {}, _retry = false) => {
  const url = createApiUrl(endpoint);
  const token = getToken();
  const config = {
    ...options,
    headers: {
      ...API_CONFIG.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (response.status === 401 && !_retry) {
      // Attempt silent token refresh (uses HttpOnly cookie)
      const user = await refresh().catch(() => null);
      if (user) {
        return apiRequest(endpoint, options, true);
      }
      // Refresh failed — show re-auth modal so the user can re-login
      // without losing current page state and unsaved work.
      try {
        await waitForReAuth();
        return apiRequest(endpoint, options, true);
      } catch {
        // User cancelled the re-auth modal; clearToken() was already called
        // by rejectReAuth() which fires AUTH_SESSION_EXPIRED_EVENT → logout.
        throw new Error('Сессия завершена');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.message || errorData.error || `HTTP ${response.status}`;
      const error = new Error(`API Error (${response.status}): ${message}`);
      error.status = response.status;
      error.payload = errorData;
      throw error;
    }

    // Если статус 204 (No Content), не пытаемся парсить JSON
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Не удалось подключиться к серверу. Проверьте, что backend запущен на ' + API_BASE_URL);
    }
    throw error;
  }
};

export default API_CONFIG;
